import { makeAutoObservable, runInAction } from 'mobx'
import api from '@/services/api'

export interface WYSIWYGExecution {
  id: string
  prompt: string
  action_types: string[]
  status: string
  local_tasks: number
  remote_tasks: number
  specialists_used: string[]
  fan_out_instances: number
  quality_score: number | null
  duration_ms: number | null
  cost_usd: number
  created_at: string
}

export interface WYSIWYGDashboard {
  total_executions: number
  executions_24h: number
  avg_latency_ms: number
  avg_quality: number
  total_cost_usd: number
  by_action_type: Record<string, number>
  by_specialist: Record<string, number>
  fan_out_usage: number
}

export interface WYSIWYGExecutionDetail {
  id: string
  prompt: string
  status: string
  plan: {
    local_tasks: Array<{ action: string; status: string; duration_ms: number }>
    remote_tasks: Array<{
      task_id: string
      action_type: string
      specialist: string
      strategy: string
      instance_count: number
      quality_score: number
      revisions: number
      duration_ms: number
    }>
  }
  graph: {
    nodes: Array<{ id: string; label: string; type: string; status: string; duration_ms: number }>
    edges: Array<{ source: string; target: string; label: string }>
  }
  telemetry: { total_tokens: number; total_cost_usd: number; total_duration_ms: number }
}

class WYSIWYGStore {
  executions: WYSIWYGExecution[] = []
  totalExecutions = 0
  selectedExecution: WYSIWYGExecutionDetail | null = null
  dashboard: WYSIWYGDashboard | null = null
  isLoading = false
  error: string | null = null

  constructor() {
    makeAutoObservable(this)
  }

  async fetchDashboard(windowHours = 24) {
    this.isLoading = true
    this.error = null
    try {
      const { data } = await api.get(`/wysiwyg/dashboard?window_hours=${windowHours}`)
      runInAction(() => {
        this.dashboard = data
        this.isLoading = false
      })
    } catch (err: any) {
      runInAction(() => {
        this.error = err.response?.data?.detail || 'Failed to fetch dashboard'
        this.isLoading = false
      })
    }
  }

  async fetchExecutions(filters?: { action_type?: string; limit?: number; offset?: number }) {
    this.isLoading = true
    this.error = null
    try {
      const params = new URLSearchParams()
      if (filters?.action_type) params.set('action_type', filters.action_type)
      if (filters?.limit) params.set('limit', String(filters.limit))
      if (filters?.offset) params.set('offset', String(filters.offset))
      const { data } = await api.get(`/wysiwyg/executions?${params}`)
      runInAction(() => {
        this.executions = data.items ?? []
        this.totalExecutions = data.total ?? 0
        this.isLoading = false
      })
    } catch (err: any) {
      runInAction(() => {
        this.executions = []
        this.error = err.response?.data?.detail || 'Failed to fetch executions'
        this.isLoading = false
      })
    }
  }

  async fetchExecutionDetail(id: string) {
    this.isLoading = true
    this.error = null
    try {
      const { data } = await api.get(`/wysiwyg/executions/${id}`)
      runInAction(() => {
        this.selectedExecution = data
        this.isLoading = false
      })
    } catch (err: any) {
      runInAction(() => {
        this.selectedExecution = null
        this.error = err.response?.data?.detail || 'Failed to fetch execution detail'
        this.isLoading = false
      })
    }
  }

  async fetchExecutionGraph(id: string) {
    try {
      const { data } = await api.get(`/wysiwyg/executions/${id}/graph`)
      if (this.selectedExecution) {
        runInAction(() => {
          this.selectedExecution!.graph = data
        })
      }
    } catch {
      // Non-fatal
    }
  }
}

export const wysiwygStore = new WYSIWYGStore()
